import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { LENS_DESIGNS, LENS_MATERIALS, LENS_SURFACINGS } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import {
  lensDesignLabel,
  lensMaterialLabel,
  lensSurfacingLabel,
  needsAddRange,
  needsCorridor,
} from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Putting a spectacle lens on file. §6.4 `CAT-010`, `CAT-011`, `CAT-014`, `CAT-031`.
 *
 * **The half of the catalogue a shop could not reach.** `insert into lens_product` appeared once
 * outside tests, in `tools/seed`, and all seven tenants on the deployment held `lenses=0`. With no
 * lens product `selection.service.ts` returns an empty list, so `LensChoice` offers nothing and no
 * quote, sale or job can name a lens: a shop could sell frames and nothing else, and a spectacle
 * lens is an optician's largest revenue line.
 *
 * **The availability range is on this form and not a later step**, because `CAT-011` is a `MUST` in
 * its own right and because Appendix V.4's publish gate inner joins `lens_availability`: a lens
 * entered without a range leaves the gate silently rather than failing it, the book publishes, the
 * lens has no price, and the first sale of it is what finds out. The server requires it for the
 * same reason, so leaving it off the form would only produce a refusal.
 *
 * **Design, material and surfacing are choosers**, which is the lesson `half_rim` taught: the
 * vocabulary is Appendix E's, and a second spelling typed by hand defeats every guard that compares
 * against the first.
 *
 * **The corridor and the add range appear only where they belong.** `CAT-010` makes a corridor a
 * progressive's property and `CAT-011` makes an add range a multifocal's, and the server refuses
 * both elsewhere. Hiding them is what stops a dispenser filling in a box that will be rejected.
 *
 * Every write goes through the queue (`OFF-001a`). Dioptric values are sent as typed and the server
 * refuses anything off the 0.25 step with the step in the sentence, because a 23514 or a
 * `DioptreError` reaches the queue as a 500 that is retried for ever with every write behind it
 * blocked.
 */

export interface LensCatalogueProps {
  /** Appendix B's `Catalogue - edit`, owner and manager. */
  readonly canEdit: boolean
  readonly onWritten?: () => void
}

/** A number from a text box, or undefined. Sent as typed; the server owns the rules. */
const num = (text: string): number | undefined => {
  const trimmed = text.trim()
  if (trimmed === '') return undefined
  const value = Number(trimmed)
  return Number.isFinite(value) ? value : Number.NaN
}

/** A comma-separated millimetre list, as typed. */
const mmList = (text: string): number[] | undefined => {
  const parts = text
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p !== '')
  if (parts.length === 0) return undefined
  return parts.map((p) => Number(p))
}

export function LensCatalogue({ canEdit, onWritten }: LensCatalogueProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [brand, setBrand] = useState('')
  const [name, setName] = useState('')
  const [manufacturer, setManufacturer] = useState('')
  const [udi, setUdi] = useState('')
  const [design, setDesign] = useState('single_vision')
  const [material, setMaterial] = useState('cr39')
  const [refractiveIndex, setRefractiveIndex] = useState('')
  const [surfacing, setSurfacing] = useState('stock')
  const [corridorOptionsMm, setCorridorOptionsMm] = useState('')
  const [minFitHeightMm, setMinFitHeightMm] = useState('')
  const [diametersMm, setDiametersMm] = useState('')
  const [sphereMin, setSphereMin] = useState('')
  const [sphereMax, setSphereMax] = useState('')
  const [cylinderMaxMagnitude, setCylinderMaxMagnitude] = useState('')
  const [addMin, setAddMin] = useState('')
  const [addMax, setAddMax] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const showsCorridor = needsCorridor(design)
  const showsAdd = needsAddRange(design)

  /**
   * The form is cleared when the write settles, not when the button is pressed.
   *
   * `OFF-001a`'s queue can refuse minutes later, and a form cleared on submit leaves the shopkeeper
   * with a refusal and nothing to correct: they would have to read a supplier sheet again.
   */
  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setDone(t('lensCatalogue.saved', { brand, name }))
    setBrand('')
    setName('')
    setManufacturer('')
    setUdi('')
    setRefractiveIndex('')
    setCorridorOptionsMm('')
    setMinFitHeightMm('')
    setDiametersMm('')
    setSphereMin('')
    setSphereMax('')
    setCylinderMaxMagnitude('')
    setAddMin('')
    setAddMax('')
    onWritten?.()
  })

  const save = useCallback(async () => {
    if (brand.trim() === '' || name.trim() === '') {
      setError(t('lensCatalogue.brandAndNameRequired'))
      return
    }
    if (refractiveIndex.trim() === '') {
      setError(t('lensCatalogue.indexRequired'))
      return
    }
    if (sphereMin.trim() === '' || sphereMax.trim() === '' || cylinderMaxMagnitude.trim() === '') {
      // `CAT-011`, and the Appendix V.4 reason in the header: a lens with no range leaves the
      // publish gate silently instead of failing it.
      setError(t('lensCatalogue.rangeRequired'))
      return
    }
    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/catalogue/lenses',
      label: t('lensCatalogue.queueLabel', { brand: brand.trim(), name: name.trim() }),
      body: {
        brand: brand.trim(),
        name: name.trim(),
        ...(manufacturer.trim() === '' ? {} : { manufacturer: manufacturer.trim() }),
        ...(udi.trim() === '' ? {} : { udi: udi.trim() }),
        design,
        material,
        refractiveIndex: num(refractiveIndex),
        surfacing,
        ...(showsCorridor && corridorOptionsMm.trim() !== ''
          ? { corridorOptionsMm: mmList(corridorOptionsMm) }
          : {}),
        ...(minFitHeightMm.trim() === '' ? {} : { minFitHeightMm: num(minFitHeightMm) }),
        ...(diametersMm.trim() === '' ? {} : { diametersMm: mmList(diametersMm) }),
        availability: {
          // Sent as typed. The server refuses anything off the 0.25 step with the step in the
          // sentence, because Appendix V.4 walks a range at 0.25 and an edge it cannot land on is
          // a gap nothing would report.
          sphereMin: num(sphereMin),
          sphereMax: num(sphereMax),
          cylinderMaxMagnitude: num(cylinderMaxMagnitude),
          ...(showsAdd && addMin.trim() !== '' ? { addMin: num(addMin) } : {}),
          ...(showsAdd && addMax.trim() !== '' ? { addMax: num(addMax) } : {}),
        },
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [
    addMax,
    addMin,
    brand,
    cylinderMaxMagnitude,
    design,
    diametersMm,
    enqueue,
    manufacturer,
    material,
    minFitHeightMm,
    name,
    refractiveIndex,
    showsAdd,
    showsCorridor,
    sphereMax,
    sphereMin,
    surfacing,
    t,
    udi,
  ])

  if (!canEdit) {
    return (
      <section className="lm-setup-panel" aria-labelledby="lens-catalogue-heading">
        <h2 id="lens-catalogue-heading" className="lm-setup-panel__heading">
          {t('lensCatalogue.heading')}
        </h2>
        {/* A form whose Save is a 403 would halt the write queue with everything behind it. */}
        <p className="lm-hint">{t('lensCatalogue.notYours')}</p>
      </section>
    )
  }

  return (
    <section className="lm-setup-panel" aria-labelledby="lens-catalogue-heading">
      <h2 id="lens-catalogue-heading" className="lm-setup-panel__heading">
        {t('lensCatalogue.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('lensCatalogue.means')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.brand')}</span>
        <input
          className="lm-input"
          dir="auto"
          value={brand}
          autoComplete="off"
          onChange={(e) => setBrand(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.name')}</span>
        <input
          className="lm-input"
          dir="auto"
          value={name}
          autoComplete="off"
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.manufacturer')}</span>
        <input
          className="lm-input"
          dir="auto"
          value={manufacturer}
          autoComplete="off"
          onChange={(e) => setManufacturer(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.udi')}</span>
        {/* `CAT-031`'s device identifier, which `decisions/0017` put on all three catalogue tables:
            a lens is a regulated device in the markets that require one. Latin whatever the page
            direction (`LOC-005`). */}
        <input
          className="lm-input"
          dir="ltr"
          value={udi}
          autoComplete="off"
          onChange={(e) => setUdi(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.design')}</span>
        {/* Appendix E's vocabulary as a chooser, which is what `half_rim` taught: a second spelling
            typed by hand defeats every guard that compares against the first. */}
        <select
          className="lm-input"
          value={design}
          onChange={(e) => setDesign(e.currentTarget.value)}
        >
          {LENS_DESIGNS.map((value) => (
            <option key={value} value={value}>
              {lensDesignLabel(t, value)}
            </option>
          ))}
        </select>
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.material')}</span>
        <select
          className="lm-input"
          value={material}
          onChange={(e) => setMaterial(e.currentTarget.value)}
        >
          {LENS_MATERIALS.map((value) => (
            <option key={value} value={value}>
              {lensMaterialLabel(t, value)}
            </option>
          ))}
        </select>
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.index')}</span>
        {/* `CAT-010` keeps the index separate from the material, because 1.60 and 1.67 are both
            high index plastic and a price book points at them separately. */}
        <input
          className="lm-input"
          dir="ltr"
          inputMode="decimal"
          value={refractiveIndex}
          autoComplete="off"
          placeholder="1.60"
          onChange={(e) => setRefractiveIndex(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.surfacing')}</span>
        <select
          className="lm-input"
          value={surfacing}
          onChange={(e) => setSurfacing(e.currentTarget.value)}
        >
          {LENS_SURFACINGS.map((value) => (
            <option key={value} value={value}>
              {lensSurfacingLabel(t, value)}
            </option>
          ))}
        </select>
      </label>

      {showsCorridor ? (
        <label className="lm-field">
          <span className="lm-field__label">{t('lensCatalogue.corridor')}</span>
          {/* Shown only for a progressive or a degressive, because `CAT-010` makes the corridor
              their property and the server refuses it elsewhere. */}
          <input
            className="lm-input"
            dir="ltr"
            value={corridorOptionsMm}
            autoComplete="off"
            placeholder="11, 14"
            onChange={(e) => setCorridorOptionsMm(e.currentTarget.value)}
          />
        </label>
      ) : null}

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.minFitHeight')}</span>
        <input
          className="lm-input"
          dir="ltr"
          inputMode="numeric"
          value={minFitHeightMm}
          autoComplete="off"
          onChange={(e) => setMinFitHeightMm(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.diameters')}</span>
        <input
          className="lm-input"
          dir="ltr"
          value={diametersMm}
          autoComplete="off"
          placeholder="65, 70"
          onChange={(e) => setDiametersMm(e.currentTarget.value)}
        />
      </label>

      <h3 className="lm-field__label">{t('lensCatalogue.rangeHeading')}</h3>
      <p className="lm-setup-panel__effects">{t('lensCatalogue.rangeMeans')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.sphereMin')}</span>
        <input
          className="lm-input"
          dir="ltr"
          inputMode="decimal"
          value={sphereMin}
          autoComplete="off"
          placeholder="-10.00"
          onChange={(e) => setSphereMin(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.sphereMax')}</span>
        <input
          className="lm-input"
          dir="ltr"
          inputMode="decimal"
          value={sphereMax}
          autoComplete="off"
          placeholder="6.00"
          onChange={(e) => setSphereMax(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('lensCatalogue.cylinderMax')}</span>
        {/* Unsigned, which the server enforces and says so: stored signed, every cylinder
            comparison fails closed and the lens is silently never offered. */}
        <input
          className="lm-input"
          dir="ltr"
          inputMode="decimal"
          value={cylinderMaxMagnitude}
          autoComplete="off"
          placeholder="4.00"
          onChange={(e) => setCylinderMaxMagnitude(e.currentTarget.value)}
        />
      </label>

      {showsAdd ? (
        <>
          <label className="lm-field">
            <span className="lm-field__label">{t('lensCatalogue.addMin')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="decimal"
              value={addMin}
              autoComplete="off"
              placeholder="0.75"
              onChange={(e) => setAddMin(e.currentTarget.value)}
            />
          </label>
          <label className="lm-field">
            <span className="lm-field__label">{t('lensCatalogue.addMax')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="decimal"
              value={addMax}
              autoComplete="off"
              placeholder="3.50"
              onChange={(e) => setAddMax(e.currentTarget.value)}
            />
          </label>
        </>
      ) : null}

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void save()
        }}
      >
        {t('lensCatalogue.save')}
      </button>

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
      {writeId === null ? null : (
        <p className="lm-hint" role="status">
          {statusLine(t, snapshot, writeId, t('lensCatalogue.queueSubject'))}
        </p>
      )}
    </section>
  )
}
